import { Fragment, useState, useEffect, useMemo } from 'react'
import { Dialog, Transition, Switch } from '@headlessui/react'
import { XMarkIcon, ScaleIcon, StarIcon as StarOutline, InformationCircleIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarSolid } from '@heroicons/react/24/solid'
import type { TenantStandardAssignment, UpdateStandardConfigRequest, FrameworkScoreDisplay } from '@/types'

interface StandardsConfigModalProps {
  isOpen: boolean
  onClose: () => void
  assignments: TenantStandardAssignment[]
  onSave: (updates: UpdateStandardConfigRequest[]) => void
  isSaving?: boolean
  /** Frameworks with scoring data (for star toggles) */
  frameworks?: FrameworkScoreDisplay[]
  /** Callback when a framework star is toggled */
  onToggleFrameworkStar?: (framework: FrameworkScoreDisplay) => void
}

interface StandardConfig {
  standardId: string
  standardCode: string
  standardName: string
  isEnabled: boolean
  importance: number // 1-10 scale
  includeInGuardianScore: boolean
}

/** Convert backend weight (0-100) to importance (1-10). Clamps to valid range. */
function weightToImportance(weight: number): number {
  // Map 0-100 → 1-10. Round to nearest integer.
  const imp = Math.round(weight / 10)
  return Math.max(1, Math.min(10, imp || 5)) // default 5 if 0/NaN
}

/** Convert importance (1-10) back to backend weight (0-100). */
function importanceToWeight(importance: number): number {
  return importance * 10
}

/** Labels for importance levels */
function importanceLabel(value: number): string {
  if (value <= 2) return 'Low'
  if (value <= 4) return 'Below Average'
  if (value <= 6) return 'Average'
  if (value <= 8) return 'Above Average'
  return 'Critical'
}

/** Color for importance level */
function importanceColor(value: number): string {
  if (value <= 2) return 'text-slate-400'
  if (value <= 4) return 'text-blue-400'
  if (value <= 6) return 'text-primary-400'
  if (value <= 8) return 'text-amber-400'
  return 'text-red-400'
}

// Metadata for each workload standard
const WORKLOAD_METADATA: Record<string, { policies: number; frameworks: string[]; description: string }> = {
  GUARDIAN_IDENTITY: {
    policies: 30,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB', 'EIDSCA'],
    description: 'Entra ID identity and access management policies',
  },
  GUARDIAN_EXCHANGE: {
    policies: 17,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB'],
    description: 'Exchange Online security policies',
  },
  GUARDIAN_DEFENDER: {
    policies: 16,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB'],
    description: 'Microsoft Defender for Office 365 policies',
  },
  GUARDIAN_TEAMS: {
    policies: 20,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB'],
    description: 'Microsoft Teams collaboration policies',
  },
  GUARDIAN_SHAREPOINT: {
    policies: 18,
    frameworks: ['CISA SCuBA', 'NIST SP 800-53', 'MCSB'],
    description: 'SharePoint and OneDrive sharing policies',
  },
}

/**
 * Preferred ordering for workloads - matches the overview page.
 * Identity first as it typically has the most policies and highest impact.
 */
const WORKLOAD_ORDER: Record<string, number> = {
  GUARDIAN_IDENTITY: 0,
  GUARDIAN_EXCHANGE: 1,
  GUARDIAN_DEFENDER: 2,
  GUARDIAN_TEAMS: 3,
  GUARDIAN_SHAREPOINT: 4,
}

import { FRAMEWORK_ORDER } from '@/lib/frameworkUtils'

/**
 * Modal for configuring which workloads to evaluate and which frameworks
 * to include in the Guardian Score.
 *
 * Two sections:
 * 1. Frameworks - Star toggle to include/exclude from Guardian Score
 * 2. Workloads - Enable/disable toggle and importance sliders (1-10)
 */
export default function StandardsConfigModal({
  isOpen,
  onClose,
  assignments,
  onSave,
  isSaving = false,
  frameworks,
  onToggleFrameworkStar,
}: StandardsConfigModalProps) {
  // Local state for editing workload configs
  const [configs, setConfigs] = useState<StandardConfig[]>([])
  const [showInfoFlyout, setShowInfoFlyout] = useState(false)

  // Sort and initialize configs from assignments
  useEffect(() => {
    if (assignments.length > 0) {
      const sorted = [...assignments]
        .sort((a, b) => {
          const orderA = WORKLOAD_ORDER[a.standardCode] ?? 99
          const orderB = WORKLOAD_ORDER[b.standardCode] ?? 99
          return orderA - orderB
        })
        .map((a) => ({
          standardId: a.standardId,
          standardCode: a.standardCode,
          standardName: a.standardName,
          isEnabled: a.isEnabled,
          importance: weightToImportance(a.weight),
          includeInGuardianScore: a.includeInGuardianScore,
        }))
      setConfigs(sorted)
    }
  }, [assignments])

  // Sort frameworks to match the overview page order
  const sortedFrameworks = useMemo(() => {
    if (!frameworks) return []
    return [...frameworks]
      .filter((f) => !f.isGuardian)
      .sort((a, b) => {
        const orderA = FRAMEWORK_ORDER[a.code] ?? 99
        const orderB = FRAMEWORK_ORDER[b.code] ?? 99
        return orderA - orderB
      })
  }, [frameworks])

  // Count starred frameworks
  const starredCount = sortedFrameworks.filter((f) => f.includeInGuardianScore).length

  // Update a specific config
  const updateConfig = (standardId: string, updates: Partial<StandardConfig>) => {
    setConfigs((prev) =>
      prev.map((c) => (c.standardId === standardId ? { ...c, ...updates } : c))
    )
  }

  // Toggle enabled state
  const toggleEnabled = (standardId: string) => {
    updateConfig(standardId, {
      isEnabled: !configs.find((c) => c.standardId === standardId)?.isEnabled,
    })
  }

  // Update importance
  const updateImportance = (standardId: string, importance: number) => {
    updateConfig(standardId, { importance })
  }

  // Handle save - convert importance back to weight for the backend
  const handleSave = () => {
    const updates: UpdateStandardConfigRequest[] = configs.map((c) => ({
      standardId: c.standardId,
      isEnabled: c.isEnabled,
      weight: importanceToWeight(c.importance),
      includeInGuardianScore: c.includeInGuardianScore,
    }))
    onSave(updates)
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-dark-900/80 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-dark-800 border border-dark-700 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-dark-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary-500/15">
                      <ScaleIcon className="h-5 w-5 text-primary-400" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-slate-100">
                      Configure Best Practice Checks
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-2 text-slate-400 hover:text-slate-200 hover:bg-dark-700 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="mt-6 space-y-6">
                  {/* Framework Scoring Section */}
                  {sortedFrameworks.length > 0 && (
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide">
                          Guardian Score Frameworks
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          Star frameworks to include them in your overall Guardian Score.
                          {starredCount > 0
                            ? ` ${starredCount} of ${sortedFrameworks.length} included.`
                            : ' No frameworks selected.'}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {sortedFrameworks.map((framework) => (
                          <div
                            key={framework.standardId}
                            className={`rounded-xl border p-3 transition-colors ${
                              framework.includeInGuardianScore
                                ? 'border-amber-500/30 bg-amber-500/5'
                                : 'border-dark-700 bg-dark-900/30'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="min-w-0 flex-1">
                                <span className="text-sm font-medium text-slate-200 block truncate">
                                  {framework.name}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {Math.round(framework.score)}% -- {framework.passedControls}/{framework.totalControls} passing
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => onToggleFrameworkStar?.(framework)}
                                className={`p-1.5 rounded-lg transition-all flex-shrink-0 ml-2 ${
                                  framework.includeInGuardianScore
                                    ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'
                                    : 'text-slate-600 hover:text-slate-400 hover:bg-dark-700'
                                }`}
                                title={
                                  framework.includeInGuardianScore
                                    ? 'Remove from Guardian Score'
                                    : 'Include in Guardian Score'
                                }
                              >
                                {framework.includeInGuardianScore ? (
                                  <StarSolid className="w-5 h-5" />
                                ) : (
                                  <StarOutline className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Workloads list */}
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide">
                          M365 Workloads
                        </h4>
                        <button
                          type="button"
                          onClick={() => setShowInfoFlyout(!showInfoFlyout)}
                          className="p-0.5 rounded text-slate-500 hover:text-primary-400 transition-colors"
                          title="What is workload importance?"
                        >
                          <InformationCircleIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Enable or disable policy evaluation per workload and set their relative importance.
                      </p>
                    </div>

                    {/* Info flyout */}
                    {showInfoFlyout && (
                      <div className="rounded-xl border border-primary-500/20 bg-primary-500/5 p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h5 className="text-sm font-medium text-primary-400">How Workload Importance Works</h5>
                          <button
                            type="button"
                            onClick={() => setShowInfoFlyout(false)}
                            className="p-0.5 rounded text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed">
                          Importance determines how much each workload contributes to your overall compliance scores.
                          A workload set to <span className="text-primary-300 font-medium">10 (Critical)</span> will
                          have more influence on your score than one set to <span className="text-primary-300 font-medium">3 (Below Average)</span>.
                        </p>
                        <p className="text-xs text-slate-400 leading-relaxed">
                          The values are relative — what matters is how they compare to each other. If all workloads
                          are set to the same number, they contribute equally. Increase a workload's importance if
                          failures there should have a bigger impact on your score.
                        </p>
                        <div className="flex items-center gap-4 pt-1 text-xs text-slate-500">
                          <span><span className="text-slate-400">1-2</span> Low</span>
                          <span><span className="text-blue-400">3-4</span> Below Avg</span>
                          <span><span className="text-primary-400">5-6</span> Average</span>
                          <span><span className="text-amber-400">7-8</span> Above Avg</span>
                          <span><span className="text-red-400">9-10</span> Critical</span>
                        </div>
                      </div>
                    )}

                    {configs.map((config) => (
                      <div
                        key={config.standardId}
                        className={`rounded-xl border p-4 transition-colors ${
                          config.isEnabled
                            ? 'border-primary-500/30 bg-dark-900/50'
                            : 'border-dark-700 bg-dark-900/30'
                        }`}
                      >
                        {/* Standard header with enable toggle */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={config.isEnabled}
                              onChange={() => toggleEnabled(config.standardId)}
                              className={`${
                                config.isEnabled ? 'bg-primary-500' : 'bg-dark-600'
                              } relative inline-flex h-5 w-9 items-center rounded-full transition-colors`}
                            >
                              <span
                                className={`${
                                  config.isEnabled ? 'translate-x-5' : 'translate-x-1'
                                } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                              />
                            </Switch>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-slate-200">
                                {config.standardName}
                              </span>
                              {/* Workload metadata */}
                              {WORKLOAD_METADATA[config.standardCode] && (
                                <div className="mt-1">
                                  <p className="text-xs text-slate-500">
                                    {WORKLOAD_METADATA[config.standardCode].description}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1.5">
                                    <span className="text-xs text-primary-400">
                                      {WORKLOAD_METADATA[config.standardCode].policies} policies
                                    </span>
                                    <span className="text-slate-600">&bull;</span>
                                    <span className="text-xs text-slate-400">
                                      {WORKLOAD_METADATA[config.standardCode].frameworks.join(', ')}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Importance slider (only if enabled) */}
                        {config.isEnabled && (
                          <div className="mt-4 pt-4 border-t border-dark-700">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-slate-500">Importance</span>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-medium ${importanceColor(config.importance)}`}>
                                  {importanceLabel(config.importance)}
                                </span>
                                <span className="text-sm font-medium text-primary-400">
                                  {config.importance}/10
                                </span>
                              </div>
                            </div>
                            <input
                              type="range"
                              min="1"
                              max="10"
                              step="1"
                              value={config.importance}
                              onChange={(e) =>
                                updateImportance(config.standardId, parseInt(e.target.value))
                              }
                              className="w-full h-2 bg-dark-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
                            />
                            <div className="relative h-4 text-xs text-slate-600 mt-1">
                              <span className="absolute left-0">1</span>
                              <span className="absolute -translate-x-1/2" style={{ left: `${((5 - 1) / (10 - 1)) * 100}%` }}>5</span>
                              <span className="absolute right-0">10</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-dark-700 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-slate-300 bg-dark-700 hover:bg-dark-600 border border-dark-600 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
